/**
 * URL to Organization Mapping
 * Maps domains/hostnames to organization IDs for multi-tenant filtering
 */
import { DEFAULT_ORG } from "../../constants";

export interface OrgMapping {
  hostname: string;
  organizationId: string;
  organizationName: string;
}

const LEGACY_ORG_ID_MAP: Record<string, string> = {
  'org-intellesphere': 'org_intellesphere',
  'org-dimentry': 'org_dimentry',
  'org-maven-builder': 'org_mavenbuilder',
  'org-zamdan': 'org_zamdan'
};

export function normalizeOrganizationId(orgId: string | null | undefined): string | null {
  if (!orgId) return null;

  const trimmed = orgId.trim();
  if (!trimmed) return null;

  return LEGACY_ORG_ID_MAP[trimmed] || trimmed;
}

// URL-to-Organization mappings
export const URL_TO_ORG_MAPPINGS: OrgMapping[] = [
  {
    hostname: 'diligent.intellesphere.com',
    organizationId: 'org_intellesphere',
    organizationName: 'Intellesphere.AI'
  },
  {
    hostname: 'diligent.dimentry.com',
    organizationId: 'org_dimentry',
    organizationName: 'Dimentry Labs'
  },
  {
    hostname: 'builder.dimentry.com',
    organizationId: 'org_mavenbuilder',
    organizationName: 'Maven Builder'
  },
  {
    hostname: 'diligent.zamdan.com',
    organizationId: 'org_zamdan',
    organizationName: 'Zamdan LLC'
  }
];

/**
 * Get organization ID from current URL
 */
export function getOrgFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  
  const hostname = window.location.hostname;
  
  // Check for exact hostname match
  const mapping = URL_TO_ORG_MAPPINGS.find(m => m.hostname === hostname);
  if (mapping) {
    return normalizeOrganizationId(mapping.organizationId);
  }
  
  // For development/localhost, check for query parameter override
  const urlParams = new URLSearchParams(window.location.search);
  const orgParam = urlParams.get('org');
  if (orgParam) {
    return normalizeOrganizationId(orgParam);
  }
  
  return normalizeOrganizationId(DEFAULT_ORG);
}

/**
 * Get organization name from URL
 */
export function getOrgNameFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  
  const hostname = window.location.hostname;
  const mapping = URL_TO_ORG_MAPPINGS.find(m => m.hostname === hostname);
  
  return mapping?.organizationName || null;
}

/**
 * Check if a project belongs to the current organization (based on URL)
 */
export function isProjectInCurrentOrg(
  project: { organizationId: string; organizationIds?: string[] }
): boolean {
  const currentOrgId = getOrgFromUrl();
  
  // If no org filter (localhost/dev), show all projects
  if (!currentOrgId) return true;
  
  // Check primary organization
  if (project.organizationId === currentOrgId) return true;
  
  // Check additional organizations
  if (project.organizationIds?.includes(currentOrgId)) return true;
  
  return false;
}

/**
 * Filter projects by current URL organization
 */
export function filterProjectsByUrl<T extends { organizationId: string; organizationIds?: string[] }>(
  projects: T[]
): T[] {
  const currentOrgId = getOrgFromUrl();
  
  // If no org filter, return all projects
  if (!currentOrgId) return projects;
  
  return projects.filter(project => {
    // Check primary organization
    if (project.organizationId === currentOrgId) return true;
    
    // Check additional organizations
    if (project.organizationIds?.includes(currentOrgId)) return true;
    
    return false;
  });
}

/**
 * Get all projects for a specific user across their accessible organizations
 */
export function getProjectsForUser<T extends { 
  organizationId: string; 
  organizationIds?: string[]; 
  teamMembers: { userId: string; role: string }[];
}>(
  allProjects: T[],
  userId: string
): T[] {
  // First filter by URL organization
  const orgProjects = filterProjectsByUrl(allProjects);
  
  // Then filter by user access
  return orgProjects.filter(project => 
    project.teamMembers.some(member => member.userId === userId)
  );
}

/**
 * Check if user has access to a project
 */
export function userHasAccessToProject(
  project: { 
    organizationId: string; 
    organizationIds?: string[]; 
    teamMembers: { userId: string; role: string }[];
  },
  userId: string
): boolean {
  // Check if project is in current org
  if (!isProjectInCurrentOrg(project)) return false;
  
  // Check if user is a team member
  return project.teamMembers.some(member => member.userId === userId);
}

/**
 * Get URL for accessing a specific organization
 */
export function getUrlForOrg(organizationId: string): string | null {
  const mapping = URL_TO_ORG_MAPPINGS.find(m => m.organizationId === organizationId);
  
  if (mapping) {
    return `https://${mapping.hostname}`;
  }
  
  // For localhost, use query parameter
  if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('org', organizationId);
    return currentUrl.toString();
  }
  
  return null;
}

/**
 * Get all organizations that a project belongs to
 */
export function getProjectOrganizations(
  project: { organizationId: string; organizationIds?: string[] },
  allOrganizations: { id: string; name: string }[]
): { id: string; name: string }[] {
  const orgIds = new Set([
    project.organizationId,
    ...(project.organizationIds || [])
  ]);
  
  return allOrganizations.filter(org => orgIds.has(org.id));
}
